import { Box, Center, Group, Loader, Tooltip } from "@mantine/core";
import { IconFileSymlink } from "@tabler/icons-react";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { PaymentIntervallAndBillingMethod } from "../../../../types/api.types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import type {
  Paragraph6ContractResponse,
  Paragraph6CreatableCredit
} from "../../Paragraph6.types";

interface GetBillingColumnProps {
  credits?: Map<string, Array<Paragraph6CreatableCredit>>;
}

function getBillingColumn({
  credits
}: GetBillingColumnProps): MRT_ColumnDef<Paragraph6ContractResponse> {
  return {
    header: "Abrechnung",
    Cell: ({ row }) => {
      if (
        row.original.billing_method_and_period !==
        PaymentIntervallAndBillingMethod.annual_based_on_measurements
      ) {
        return;
      }

      if (!credits) {
        return (
          <Center>
            <Loader size="xs" />
          </Center>
        );
      }

      const creatableCreditsForContract = credits.get(row.original.id) ?? [];

      if (creatableCreditsForContract.length === 0) {
        const billingPeriodStart = backendDateOrDateTimeToLuxonDateTime(
          row.original.billing_period_start
        );
        return `jährlich ab ${billingPeriodStart.toFormat("dd. MMM")}`;
      }

      const oldestCreateableCredit = creatableCreditsForContract.reduce(
        (oldest, current) => {
          if (!oldest) {
            return current;
          }
          return new Date(current.end_date) < new Date(oldest.end_date)
            ? current
            : oldest;
        }
      );
      const creationDateForOldestCreateableCredit =
        backendDateOrDateTimeToLuxonDateTime(
          oldestCreateableCredit.end_date
        )?.plus({ days: 1 });

      return (
        <Group className="credits-available-cell" wrap="nowrap">
          <Box w={130}>
            jährlich ab{" "}
            {creationDateForOldestCreateableCredit.toFormat("dd. MMM")}
          </Box>
          <Tooltip label="Eine Gutschrift kann erstellt werden">
            <IconFileSymlink
              color="var(--mantine-color-primary-filled)"
              size="1.4em"
            />
          </Tooltip>
        </Group>
      );
    }
  };
}

export { getBillingColumn };
